'use client';
import { Link } from '@/features/uiKit/Link';
import { Stack } from '@/features/uiKit/Stack';
import { Text } from '@/features/uiKit/Text';
import { landingContentFrame } from './landingSettings';
import {
  fullEnglishLanguageName,
  SupportedLanguage,
  supportedLanguages,
} from '@/features/Lang/lang';
import { getUrlStart, getUrlStartWithoutLastSlash } from '../Lang/getUrlStart';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLingui } from '@lingui/react';
import { Suspense } from 'react';

interface FooterProps {
  lang: SupportedLanguage;
}
const FooterComponent: React.FC<FooterProps> = ({ lang }) => {
  const textColor = '#111';
  const { i18n } = useLingui();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const onlyPathname = pathname || '/';
  const onlyQuery = searchParams.toString() || '';

  const pathNameAndQuery = onlyPathname + (onlyQuery ? `?${onlyQuery}` : '');

  const pathnameWithoutLang =
    lang === 'en' && !pathNameAndQuery.startsWith('/en')
      ? pathNameAndQuery || '/'
      : pathNameAndQuery.replace(`/${lang}`, '') || '/';

  const pathWithoutFirstSlash = pathnameWithoutLang.startsWith('/')
    ? pathnameWithoutLang.slice(1)
    : pathnameWithoutLang;
  return (
    <Stack
      component={'footer'}
      sx={{
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '50px 0 50px 0',
        //backgroundColor: "#070f1a",
        backgroundColor: '#fff',
        color: textColor,
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        '& a': {
          color: textColor,
          textDecorationColor: 'rgba(0, 0, 0, 0.35)',
        },
        marginTop: '0px',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Stack
        sx={{
          ...landingContentFrame,
          display: 'grid',
          gridTemplateColumns: '1fr 1.5fr 1fr',
          alignItems: 'center',
          gap: '25px',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 9999,
          '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
            gap: '70px',
          },
        }}
      >
        <Stack
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '10px',
            a: {
              fontWeight: 500,
              color: textColor,
              textUnderlineOffset: '3px',
              textDecorationColor: 'rgba(0, 0, 0, 0.35)',
            },
          }}
        >
          <Link href={`${getUrlStart(lang)}scenarios`} variant="body1">
            {i18n._(`Role-Play`)}
          </Link>
          <Link href={`${getUrlStart(lang)}contacts`} variant="body1">
            {i18n._(`Contacts`)}
          </Link>
          <Link href={`${getUrlStart(lang)}blog`} variant="body1">
            {i18n._(`Blog`)}
          </Link>
          <Link href={`${getUrlStart(lang)}pricing`} variant="body1">
            {i18n._(`Price`)}
          </Link>
        </Stack>

        <Stack
          sx={{
            gap: '10px',
            alignItems: 'center',
            '@media (max-width: 900px)': {
              alignItems: 'flex-start',
            },
          }}
        >
          <a href={`${getUrlStartWithoutLastSlash(lang)}`}>
            <img
              src="/logo-dark.svg"
              alt="Online English Learning"
              width="200px"
              height="37px"
              style={{
                opacity: 0.92,
              }}
            />
          </a>

          <Stack
            sx={{
              flexDirection: 'row',
              gap: '0 10px',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              '@media (max-width: 900px)': {
                justifyContent: 'flex-start',
              },
            }}
          >
            {supportedLanguages.map((lang, index) => {
              const href = [getUrlStartWithoutLastSlash(lang), pathWithoutFirstSlash]
                .filter((part) => part)
                .join('/')
                .replace(/\/+/g, '/');
              return (
                <Link
                  key={lang}
                  href={href}
                  variant="body1"
                  lang={lang}
                  aria-label={`Switch to ${fullEnglishLanguageName[lang]}`}
                >
                  {fullEnglishLanguageName[lang]}
                  {index !== supportedLanguages.length - 1 && ','}
                </Link>
              );
            })}
          </Stack>
        </Stack>
        <Stack
          sx={{
            alignItems: 'flex-end',
            justifyContent: 'center',
            '@media (max-width: 900px)': {
              alignItems: 'flex-start',
            },
          }}
        >
          <Text color="inherit">FluencyPal</Text>
          <Text color="inherit">© 2026</Text>
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '2px 10px',
              flexWrap: 'wrap',
            }}
          >
            <Link
              href={`${getUrlStart(lang)}terms`}
              variant="body1"
              align="right"
              sx={{
                color: textColor,
              }}
            >
              {i18n._(`Terms of Use`)}
            </Link>
            <Link
              href={`${getUrlStart(lang)}privacy`}
              variant="body1"
              align="right"
              sx={{
                color: textColor,
              }}
            >
              {i18n._(`Privacy Policy`)}
            </Link>
            <Link
              href={`${getUrlStart(lang)}cookies`}
              variant="body1"
              align="right"
              sx={{
                color: textColor,
              }}
            >
              {i18n._(`Cookies Policy`)}
            </Link>
            <Link
              href={`${getUrlStart(lang)}community-rules`}
              variant="body1"
              align="right"
              sx={{
                color: textColor,
              }}
            >
              {i18n._(`Community rules`)}
            </Link>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const Footer: React.FC<FooterProps> = ({ lang }) => {
  return (
    <Suspense>
      <FooterComponent lang={lang} />
    </Suspense>
  );
};
